import { buildSearch, DEV_PARAM_DEFS, isDevMode, withParam, type DevParamDef } from "../devParams";
import type { Player } from "../engine/player";

const COLLAPSE_KEY = "bullettime-dev-panel-collapsed";

/** Dev-only control panel: URL param controls (changing one navigates immediately),
 *  perf stats fed by DevPerfOverlay, seed tools, and preset links. Singleton — owns
 *  all dev DOM across game sessions. */
export class DevPanel {
	private readonly root = document.createElement("div");
	private readonly titleEl = document.createElement("span");
	private readonly headerFpsEl = document.createElement("span");
	private readonly statsEl = document.createElement("div");
	private readonly errorEl = document.createElement("div");
	private readonly seedValueEl = document.createElement("span");
	private readonly seedButtons: HTMLButtonElement[] = [];
	private levelInput: HTMLInputElement | null = null;
	private currentSeed: string | null = null;
	private readonly invulnerableInput = document.createElement("input");
	private player: Player | null = null;

	constructor() {
		this.root.className = "dev-panel";
		if (localStorage.getItem(COLLAPSE_KEY) === "true") this.root.classList.add("collapsed");
		// Panel inputs must not reach the game's document-level key handlers (WASD would move the player).
		this.root.addEventListener("keydown", (e) => e.stopPropagation());
		this.root.addEventListener("keyup", (e) => e.stopPropagation());

		const header = document.createElement("div");
		header.className = "dev-panel-header";
		this.headerFpsEl.className = "dev-panel-fps";
		header.append(this.titleEl, this.headerFpsEl);
		header.addEventListener("click", () => {
			const collapsed = this.root.classList.toggle("collapsed");
			localStorage.setItem(COLLAPSE_KEY, String(collapsed));
			this.updateTitle();
		});
		this.updateTitle();

		const body = document.createElement("div");
		body.className = "dev-panel-body";
		this.statsEl.className = "dev-panel-stats";
		body.append(this.statsEl, this.buildLiveControls(), this.buildControls(), this.buildSeedRow(), this.buildPresets());

		this.root.append(header, body);
		this.errorEl.className = "dev-error-overlay";
		document.body.append(this.root, this.errorEl);
	}

	updateStats(lines: string[]): void {
		this.statsEl.textContent = lines.join("\n");
		this.headerFpsEl.textContent = lines[0] ?? "";
	}

	clearStats(): void {
		this.updateStats([]);
	}

	setEngineErrors(count: number): void {
		if (count > 0) {
			this.errorEl.textContent = `⚠ ${count} engine error${count > 1 ? "s" : ""} — check console`;
			this.errorEl.style.display = "block";
		} else {
			this.errorEl.style.display = "none";
		}
	}

	setCurrentSeed(seed: string): void {
		this.currentSeed = seed;
		this.seedValueEl.textContent = seed;
		for (const button of this.seedButtons) button.disabled = false;
	}

	attachPlayer(player: Player): void {
		this.player = player;
		this.invulnerableInput.disabled = false;
		this.invulnerableInput.checked = player.isInvulnerable;
	}

	detachPlayer(): void {
		this.player = null;
		this.invulnerableInput.disabled = true;
		this.invulnerableInput.checked = false;
	}

	private updateTitle(): void {
		this.titleEl.textContent = this.root.classList.contains("collapsed") ? "▸ DEV" : "▾ DEV";
	}

	private navigate(key: string, value: string | null): void {
		window.location.assign(window.location.pathname + withParam(window.location.search, key, value));
	}

	private navigateToSearch(search: string): void {
		window.location.assign(window.location.pathname + search);
	}

	private buildLiveControls(): HTMLElement {
		const row = document.createElement("label");
		row.className = "dev-panel-row";
		row.title = "Player refuses all damage. Live toggle for the current game only — no reload.";
		preventFocusSteal(row);
		this.invulnerableInput.type = "checkbox";
		this.invulnerableInput.disabled = true;
		this.invulnerableInput.addEventListener("change", () => {
			if (this.player) this.player.isInvulnerable = this.invulnerableInput.checked;
		});
		row.append(this.invulnerableInput, document.createTextNode("Invulnerable"));
		return row;
	}

	private buildControls(): HTMLElement {
		const params = new URLSearchParams(window.location.search);
		const container = document.createElement("div");
		container.className = "dev-panel-controls";
		for (const def of DEV_PARAM_DEFS) {
			container.appendChild(this.buildControl(def, params));
		}
		return container;
	}

	private buildControl(def: DevParamDef, params: URLSearchParams): HTMLElement {
		const row = document.createElement("label");
		row.className = "dev-panel-row";
		row.title = def.description;
		const current = params.get(def.key);

		if (def.type === "boolean") {
			preventFocusSteal(row);
			const input = document.createElement("input");
			input.type = "checkbox";
			input.checked = current === "true";
			input.addEventListener("change", () => this.navigate(def.key, input.checked ? "true" : null));
			row.append(input, document.createTextNode(def.label));
			return row;
		}

		const input = document.createElement("input");
		input.type = def.type === "int" ? "number" : "text";
		if (def.type === "int") input.min = "1";
		input.value = current ?? "";
		if (def.requiresImmediateStart && !hasImmediateStart(params)) input.disabled = true;
		input.addEventListener("change", () => this.navigate(def.key, input.value.trim() || null));
		input.addEventListener("keydown", (e) => {
			if (e.key === "Enter") input.blur();
		});
		if (def.key === "level") this.levelInput = input;
		row.append(document.createTextNode(`${def.label} `), input);
		return row;
	}

	private buildSeedRow(): HTMLElement {
		const row = document.createElement("div");
		row.className = "dev-panel-row";
		this.seedValueEl.className = "dev-panel-seed-value";
		this.seedValueEl.textContent = "—";
		this.seedValueEl.title = "Current game seed";

		const copy = this.button("copy", "Copy the current game seed", () => {
			if (this.currentSeed) navigator.clipboard.writeText(this.currentSeed);
		});
		const same = this.button("↻ same", "Restart with the current game's seed", () => {
			if (this.currentSeed) this.navigate("seed", this.currentSeed);
		});
		const fresh = this.button("↻ new", "Restart with a random seed", () => this.navigate("seed", null));
		copy.disabled = same.disabled = true;
		this.seedButtons.push(copy, same);

		row.append(this.seedValueEl, copy, same, fresh);
		return row;
	}

	private buildPresets(): HTMLElement {
		const row = document.createElement("div");
		row.className = "dev-panel-row";
		row.append(
			this.button("playground", "?dev=true&playground=true", () =>
				this.navigateToSearch(buildSearch({ dev: "true", playground: "true" })),
			),
			this.button("level ▶", "Skip menu straight into the level from the Level field", () =>
				this.navigateToSearch(
					buildSearch({ dev: "true", no_menu: "true", level: this.levelInput?.value.trim() || "1" }),
				),
			),
			this.button("prod", "Plain URL, no params — the panel disappears", () => this.navigateToSearch("")),
		);
		return row;
	}

	private button(label: string, title: string, onClick: () => void): HTMLButtonElement {
		const button = document.createElement("button");
		button.textContent = label;
		button.title = title;
		preventFocusSteal(button);
		button.addEventListener("click", onClick);
		return button;
	}
}

/** Suppresses focus acquisition on click (toolbar-button pattern) so game keyboard
 *  control is never interrupted; the click itself still fires. The focusin listener is
 *  needed too: a click on a label's text forwards activation to its checkbox and focuses
 *  it after mousedown, bypassing the preventDefault. */
function preventFocusSteal(el: HTMLElement): void {
	el.addEventListener("mousedown", (e) => e.preventDefault());
	el.addEventListener("focusin", (e) => (e.target as HTMLElement).blur());
}

function hasImmediateStart(params: URLSearchParams): boolean {
	return params.get("no_menu") === "true" || params.get("playground") === "true";
}

let instance: DevPanel | null = null;

export function getDevPanel(): DevPanel | null {
	if (!isDevMode()) return null;
	if (!instance) instance = new DevPanel();
	return instance;
}
